<script lang="ts">
  import type { OPDSError } from '../stores/opds.svelte';

  interface Props {
    error: OPDSError;
    onDismiss: () => void;
  }

  let { error, onDismiss }: Props = $props();

  const errorIcons: Record<OPDSError['type'], string> = {
    cors: '🚫',
    network: '📡',
    http: '⚠️',
    timeout: '⏱️',
    parse: '📄',
    unknown: '❓',
  };

  const errorColors: Record<OPDSError['type'], string> = {
    cors: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
    network: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800',
    http: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800',
    timeout: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
    parse: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800',
    unknown: 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800',
  };
</script>

<div
  class="error-alert rounded-lg border-2 p-4 mb-4 {errorColors[error.type]}"
  role="alert"
>
  <div class="flex items-start gap-3">
    <!-- Icon -->
    <div class="text-2xl flex-shrink-0 mt-0.5">
      {errorIcons[error.type]}
    </div>

    <!-- Content -->
    <div class="flex-1 min-w-0">
      <div class="flex items-center justify-between gap-2 mb-1">
        <h3 class="font-semibold text-gray-900 dark:text-gray-100">
          {error.message}
        </h3>
        <button
          onclick={onDismiss}
          class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors flex-shrink-0"
          aria-label="Dismiss error"
        >
          <svg
            class="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {#if error.details}
        <p class="text-sm text-gray-700 dark:text-gray-300 mb-2">
          {error.details}
        </p>
      {/if}

      {#if error.url}
        <div class="text-xs text-gray-500 dark:text-gray-400 font-mono break-all">
          URL: {error.url}
        </div>
      {/if}

      {#if error.statusCode}
        <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Status code: {error.statusCode}
        </div>
      {/if}

      <!-- Helpful suggestions based on error type -->
      {#if error.type === 'cors'}
        <div class="mt-3 p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
          <p class="text-xs text-gray-600 dark:text-gray-400">
            <strong>💡 Solutions:</strong><br />
            • Try accessing this catalog from the native mobile app<br />
            • Contact the catalog owner to enable CORS for web browsers<br />
            • Some catalogs only work with specific apps
          </p>
        </div>
      {:else if error.type === 'timeout'}
        <div class="mt-3 p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
          <p class="text-xs text-gray-600 dark:text-gray-400">
            <strong>💡 Try:</strong><br />
            • Wait a moment and try again<br />
            • Check your internet connection<br />
            • The server might be temporarily slow
          </p>
        </div>
      {:else if error.type === 'http' && error.statusCode === 404}
        <div class="mt-3 p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
          <p class="text-xs text-gray-600 dark:text-gray-400">
            <strong>💡 Possible causes:</strong><br />
            • The catalog URL may have changed<br />
            • The catalog may have been removed<br />
            • Check if the URL is correct
          </p>
        </div>
      {/if}
    </div>
  </div>
</div>

<style>
  .error-alert {
    animation: slideIn 0.3s ease-out;
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
</style>
